import { useEffect, useRef, useState } from 'react';
import { ErrorMessage, Form, Formik, FormikHelpers, FormikProps } from "formik";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../../core/store/store";
import { OTPFormValues, otpValidationSchema } from "../../../auth/presentation/interfaces/OTPFormInterface";
import { onLoginUser, onRemoveAccount } from "../../../auth/presentation/store/auth/AuthThunk";
import { ContainerModal } from "../components/ContainerModal";
import OTPInput from "react-otp-input";
import { CustomButton } from "../../../../core/components/CustomButton";
import { TimerComponent } from "../../../auth/presentation/components/TimerComponent";
import { Column } from "../../../../core/components/Column";
import useLogout from "../../../../core/hooks/useLogout";

interface OtpVerificationProps {
  isOpen: boolean;
  onRequestClose: () => void;
  phoneNumber?: string;
  email?: string;
  type: '0' | '1' | '3';
  onVerificationSuccess?: () => void;
}

const initialValues: OTPFormValues = {
  otp: '',
  password: '',
  confirmPassword: '',
};

export const OtpVerification: React.FC<OtpVerificationProps> = ({ isOpen, onRequestClose, phoneNumber, email, type,onVerificationSuccess  }) => {
  const dispatch: AppDispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const formikRef = useRef<FormikProps<OTPFormValues>>(null);
  const [showResendOTP, setShowResendOTP] = useState(true);
  const logout = useLogout();
  useEffect(() => {
    if (isOpen && formikRef.current) {
      formikRef.current.resetForm();
    }
    setErrorMessage(null);
  }, [isOpen]);

  const handleSubmit = async (values: OTPFormValues, { resetForm  }: FormikHelpers<OTPFormValues>) => {
    setErrorMessage(null);
    setIsLoading(true);

    if (type === '3'){
      await dispatch(onRemoveAccount({otp: values.otp})).then((resp) => {
        if (resp.meta.requestStatus === 'fulfilled') {
          onRequestClose();
          resetForm();
          logout();
        } else {
          setErrorMessage("Invalid OTP");
        }
      });
    } else {
      await dispatch(
        onLoginUser({
          emailOrPhone: type === '1' ? phoneNumber ?? '' : email ?? '',
        type,
        otp: values.otp,
        })
      ).then((resp) => {
        if (resp.meta.requestStatus === 'fulfilled') {
          if (onVerificationSuccess) {
            onVerificationSuccess();
          }
          onRequestClose();
          resetForm();
        } else {
          if (resp.meta.requestStatus === 'rejected') {
            if ((resp as any).error.message === "400") {
              setShowResendOTP(false);
              if (type === '1') {
                setErrorMessage("This number has already been verified on a different account.");
              } else if (type === '0') {
                setErrorMessage("This email has already been verified on a different account.");
              }
            }
            if ((resp as any).error.message === "401") {
              setErrorMessage("Invalid OTP");
            }
          }
        }
      });
    }
    setIsLoading(false);
  };
  const verificationContact = type === '3' ? phoneNumber || email : type === '1' ? phoneNumber : email;

  const handleClose = () => {
    onRequestClose();
    setShowResendOTP(true);
  }

  return (
    <Formik
    innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={otpValidationSchema()}
      validateOnBlur
      validateOnChange={false}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, setFieldValue }: FormikProps<OTPFormValues>) => (
        <ContainerModal
          isOpen={isOpen}
          onRequestClose={handleClose}
          title="OTP verification"
          subtitle={`We have sent a verification code to ${verificationContact}`}
        >
          <Form>
            <Column crossAxisAlignment="center" className="mt-6">
              <OTPInput
                value={values.otp}
                onChange={(otp) => setFieldValue('otp', otp)}
                numInputs={6}
                renderSeparator={<span className="w-2 md:w-4"></span>}
                renderInput={(props) => <input {...props} inputMode="numeric" pattern="[0-9]*" />}
                inputStyle={{
                  width: '40px',
                  height: '40px',
                  padding: '1rem 0.5rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #E6E6E6',
                }}
              />
            </Column>
            
            <div className="mt-7 mx-6 sm:mx-12">
              {errorMessage && <div className="text-red-500 mt-2">{errorMessage}</div>}
              {showResendOTP && <TimerComponent phoneOrEmail={verificationContact} type={type} />}
            </div>
            <div className="absolute sm:relative sm:mt-10 sm:mb-16 bottom-0 left-0 right-0 p-4">
              <ErrorMessage name="otp" component="div" className="text-red-500 mb-2" />
              <CustomButton
                type={errorMessage ? "button" : "submit"}
                className="w-full"
                onClick={errorMessage ? handleClose : undefined}
                color={errorMessage ? "white" : undefined}
                loading={!errorMessage && isLoading}
              >
                {errorMessage ? "Close" : "Submit"}
              </CustomButton>
            </div>
          </Form>
        </ContainerModal>
      )}
    </Formik>
  );
};
